import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { ItemStatus, Point, PointLocation } from '../../../interfaces/custom-route.interface';
import { PlannerDayStateService } from '../../planner/services';
import { BreakpointsService } from 'src/app/services/breakpoints.service';
import { interval, Subscription } from 'rxjs';
import { CustomMarkerOptions } from '../interfaces/model';
import dayjs from 'dayjs';
import { EditRouteService } from '../../planner/components/route-timeline/components/daily-route-list/services';

const MIN_SEG_M     = 0.05;  // ignora segmentos < 5 cm
const AHEAD_M       = 7;    // “look-ahead” para heading (m)
const EPS_M         = 1e-6;  // tolerancia numérica

@Injectable({ providedIn: 'root' })

export class RouteDataService {
    private _plannerDayStateService: PlannerDayStateService = inject(PlannerDayStateService);
    private _breakpointsService: BreakpointsService = inject(BreakpointsService);
    private _editRouteService = inject(EditRouteService);
    
    private _editedCustomRouteData = this._editRouteService.editedCustomRouteData;
    public currentDayIndex = this._plannerDayStateService.dayToShowIndex;
    
    public currentDate = computed<Date | null>(() => {
        const date = this._editedCustomRouteData()?.dailyRoutes?.[this.currentDayIndex()]?.date;
        return date ? new Date(date) : null;
    });
    public startPoint = computed<google.maps.LatLngLiteral>(() => this._editedCustomRouteData()?.dailyRoutes?.[this.currentDayIndex()]?.startPoint);
    public endPoint = computed<google.maps.LatLngLiteral>(() => this._editedCustomRouteData()?.dailyRoutes?.[this.currentDayIndex()]?.endPoint);
    public startPointLocation = computed<PointLocation | undefined>(() => this._editedCustomRouteData()?.dailyRoutes?.[this.currentDayIndex()]?.startPoint);
    public endPointLocation = computed<PointLocation | undefined>(() => this._editedCustomRouteData()?.dailyRoutes?.[this.currentDayIndex()]?.endPoint);
    public firstPointLocation = computed<google.maps.LatLngLiteral | undefined>(() => {
        const points = this._editedCustomRouteData()?.dailyRoutes?.[this.currentDayIndex()]?.points;
        return points?.length ? points[0].currentLocation : undefined;
    });
    public lastPointLocation = computed<google.maps.LatLngLiteral | undefined>(() => {
        const points = this._editedCustomRouteData()?.dailyRoutes?.[this.currentDayIndex()]?.points;
        return points?.length ? points[points.length - 1].currentLocation : undefined;
    });
    public endPolylinePath = computed<google.maps.LatLngLiteral[]>(() => this._editedCustomRouteData()?.dailyRoutes?.[this.currentDayIndex()]?.endPolylinePath ?? []);
    private routeSegments = signal<Point[][]>([]);

    public getRouteSegments = computed<Point[][]>(() => this.routeSegments());
    public getCurrentRouteDay = computed<Point[]>(() => this.routeSegments()?.[this.currentDayIndex()]);
    public getTotalTramesFromCurrentRouteDay = computed<number>(() => this.routeSegments()?.[this.currentDayIndex()]?.length - 1)

    private intervalSubscription: Subscription = null;
    private geolocationEnabled = signal<boolean>(false);
    private now = signal<dayjs.Dayjs>(null);
    public getCurrentTime = computed<dayjs.Dayjs>(() => this.now());
    public isMobile = computed<boolean>(() => this._breakpointsService.isMobile());
    public isGeolocationActive = computed<boolean>(() => this.isToday() && this.isMobile() && this.geolocationEnabled());

    private currentTrameIndex = signal<number>(undefined);
    public getCurrentTrameIndex = computed(() => this.currentTrameIndex());
    private currentStepIndex = signal<number>(undefined);
    public getCurrentStepIndex = computed(() => this.currentStepIndex());
    private activeSegmentByHour = signal<boolean>(false);
    public getActiveSegmentByHour = computed(() => this.activeSegmentByHour());
    private reloadMap = signal(false);
    public getReloadMap = computed(() => this.reloadMap());

    public showDevPanel = signal(false);

    private _updateRouteEffect = effect(async () => {
        const data = this._editedCustomRouteData();
        if (!data?.dailyRoutes) return;

        await this.processRouteGeometry();
        this.resetCurrentStepAndTrame();
        this.enableReloadMap();
    }, { allowSignalWrites: true })

    public setRouteSegments(routeSegments: Point[][]): void {
        this.routeSegments.set(routeSegments);
    }

    public resetMarkersStatus(): void {
        this.routeSegments?.update((prev: Point[][]) =>
            prev?.map((segmentArr) =>
                segmentArr?.map((item) => ({
                    ...item,
                    status: ItemStatus.PENDING,
                    // status: this.getItemStatusFromCurrentDate(item.endDate, ItemStatus.PENDING)
                }))
            )
        );
    }

    public setPolylineStatusOnTrameIndex(trameIndex: number = null): void {
        let hasChanges: boolean = false;
    
        this.routeSegments.update((prev: Point[][]) => {
          const segmentArr: Point[] = prev?.[this.currentDayIndex()];
          const newSegmentArr: Point[] = segmentArr?.map((element, index) => {
              let newStatus: ItemStatus;
    
              if (index === trameIndex)
                newStatus = ItemStatus.RUNNING;
              else if (index < trameIndex)
                newStatus = ItemStatus.FINISHED;
              else {
                newStatus = ItemStatus.PENDING;
              }
    
              newStatus = this.getItemStatusFromCurrentDate(element.endDate, newStatus);

              if (element.status !== newStatus) {
                hasChanges = true;
                return { ...element, status: newStatus };
              }
    
              return element;
          });
    
          if (!hasChanges)
            return prev;
    
          return prev.map((arr, idx) =>  idx === this.currentDayIndex() ? newSegmentArr : arr);
        });
    }

    public getItemStatusFromCurrentDate(date: Date, prevStatus: ItemStatus){
        const now = new Date();
        return date < now ? ItemStatus.FINISHED : prevStatus
    }

    public setActiveRouteSegmentByHour(hour: dayjs.Dayjs): number {
        let hasChanges: boolean = false;
        let currentTrame: number = 0;
    
        this.routeSegments?.update((prev: Point[][]) => {
            const segmentArr: Point[] = prev?.[this.currentDayIndex()];
            const newSegmentArr: Point[] = segmentArr?.map((item, stepIdx) => {
                const startTime: Date | dayjs.Dayjs = segmentArr?.[stepIdx - 1]?.endDate ?? dayjs().hour(8).minute(0);
                const endTime: Date = item.endDate;
                const newStatus = this.checkHour(startTime, endTime, hour);
                if (newStatus === ItemStatus.RUNNING)
                    currentTrame = stepIdx
    
                if (newStatus !== item.status) {
                    hasChanges = true;
                    return { ...item, status: newStatus };
                }
    
                return item;
            });
    
            if (!hasChanges) 
                return prev;
    
            return prev.map((arr, idx) => idx === this.currentDayIndex() ? newSegmentArr : arr);
        });

        return currentTrame;
    }

    public checkHour(startTime: Date | dayjs.Dayjs, endTime: Date | dayjs.Dayjs, hour: dayjs.Dayjs): ItemStatus {
        const toMinutes = ((time: dayjs.Dayjs) => time.hour() * 60 + time.minute());
        const actualMin: number = toMinutes(dayjs(hour));
        const startMin: number  = toMinutes(dayjs(startTime));
        const endMin: number    = toMinutes(dayjs(endTime));
    
        if (actualMin >= startMin && actualMin < endMin)
            return ItemStatus.RUNNING;
    
        else if (actualMin < startMin)
            return ItemStatus.PENDING;
    
        else
            return ItemStatus.FINISHED;
    }

    public isToday(): boolean {
        const today: Date = new Date();
        return today.getFullYear() === this.currentDate()?.getFullYear() && 
            today.getMonth() === this.currentDate()?.getMonth() && 
            today.getDate() === this.currentDate()?.getDate()
    }

    public isYesterdayOrOlder(date: Date) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const input = new Date(date);
        input.setHours(0, 0, 0, 0); 
        return input < today;
    }

    public enableGeolocation(): void{
        this.geolocationEnabled.set(true);
    }

    public disableGeolocation(): void{
        this.geolocationEnabled.set(false);
    }

    public startInterval(): void {
        if (this.intervalSubscription) 
            return;

        this.now.set(dayjs());
        this.intervalSubscription = interval(1000).subscribe(() => {
            this.now.set(dayjs());
        });
    }

    public stopInterval(): void {
        if (!this.intervalSubscription)
            return;

        this.intervalSubscription.unsubscribe();
        this.intervalSubscription = null;
    }

    async processRouteGeometry(): Promise<void> {
        await google.maps.importLibrary('geometry');
        const transfersPathDistances = this._editedCustomRouteData()?.dailyRoutes?.map(e => {
            return e.points?.map(point => {
            // const status = this.getItemStatusFromCurrentDate(point.endDate, ItemStatus.PENDING)
            return {
                ...point,
                status: ItemStatus.PENDING,
                transferPathDistances: point.transfers?.map(t => 
                    this.getNearestItemInfo(t.location, point.polylinesPath)?.distanceAlongPath
                )
            }
            })
        })
        this.setRouteSegments(transfersPathDistances);
    }

    public setCurrentStepIndex(index: number){
        this.currentStepIndex.set(index);
    }

    public increaseCurrentStepIndex(){
        this.currentStepIndex.update(prev => prev + 1);
    }

    public decreaseCurrentStepIndex(){
        this.currentStepIndex.update(prev => prev - 1);
    }

    public setCurrentTrameIndex(index: number){
        this.currentTrameIndex.set(index);
    }

    public increaseCurrentTrameIndex(){
        this.currentTrameIndex.update(prev => prev + 1);
    }

    public decreaseCurrentTrameIndex(){
        this.currentTrameIndex.update(prev => prev - 1);
    }

    public setActiveSegmentByHour(active: boolean){
        this.activeSegmentByHour.set(active)
    }

    public resetCurrentStepAndTrame(){
        this.currentStepIndex.set(undefined);
        this.currentTrameIndex.set(undefined);
        this.setActiveSegmentByHour(false);
    }

    public enableReloadMap(){
        this.reloadMap.set(true);
    }

    public disableReloadMap(){
        this.reloadMap.set(false);
    }

    public resetRouteSegments(){
        this.routeSegments.set([]);
    }


    public getNearestItemInfo(point: google.maps.LatLngLiteral, rawPath: google.maps.LatLngLiteral[], nextPoint?: google.maps.LatLngLiteral): CustomMarkerOptions {
        const path: google.maps.LatLngLiteral[] = rawPath;
        // const path: google.maps.LatLngLiteral[] = [];
        // rawPath.forEach(pt => {
        //     if (!path.length || this.dist(pt, path[path.length - 1]) > MIN_SEG_M) 
        //         path.push(pt);
        // });

        if (!path.length)
            throw new Error('Polyline vacía');

        if (path.length === 1) {
            return {
                itemLocation: path[0],
                itemHeading: nextPoint
                ? google.maps.geometry.spherical.computeHeading(
                    new google.maps.LatLng(path[0]),
                    new google.maps.LatLng(nextPoint)
                    )
                : 0,
                itemDistance: this.dist(point, path[0]),
                distanceAlongPath: 0
            };
        }

        let bestSeg = 0, bestT = 0, bestQ = path[0];
        let bestPerp = Number.POSITIVE_INFINITY;
        let bestAlong = 0, accLen = 0;
        for (let i = 0; i < path.length - 1; i++) {
            const a = path[i], b = path[i + 1];
            const segLen = this.dist(a, b);
            if (segLen < MIN_SEG_M) 
                continue;

            const { q, t } = this.projectOnSegment(point, a, b);
            const perp = this.dist(point, q);
            if (perp < bestPerp - EPS_M) {
                bestPerp  = perp;
                bestSeg   = i;
                bestT     = t;
                bestQ     = q;
                bestAlong = accLen + t * segLen;
            }
            accLen += segLen;
        }

        let remaining = (1 - bestT) * this.dist(path[bestSeg], path[bestSeg + 1]);
        let j = bestSeg + 1;
        while (j < path.length - 1 && remaining < AHEAD_M) {
            remaining += this.dist(path[j], path[j + 1]);
            j++;
        }

        let ahead: google.maps.LatLngLiteral;
        if (remaining < AHEAD_M) 
            ahead = path[path.length - 1];
        else {
            const prev  = path[j - 1];
            const next  = path[j];
            const extra = remaining - AHEAD_M;
            const seg   = this.dist(prev, next) || EPS_M;
            const frac  = (seg - extra) / seg;
            const aheadLL = google.maps.geometry.spherical.interpolate(
                new google.maps.LatLng(prev),
                new google.maps.LatLng(next),
                frac
            );
            ahead = { lat: aheadLL.lat(), lng: aheadLL.lng() };
        }

        let heading: number;
        if (nextPoint) {
            heading = google.maps.geometry.spherical.computeHeading(
                new google.maps.LatLng(bestQ),
                new google.maps.LatLng(nextPoint)
            );
        } 
        else {
            heading = google.maps.geometry.spherical.computeHeading(
                new google.maps.LatLng(bestQ),
                new google.maps.LatLng(ahead)
            );
        }
        heading = (heading + 360) % 360; 

        return {
            itemLocation: bestQ,
            itemHeading: heading,
            itemDistance: bestPerp,
            distanceAlongPath: bestAlong
        };
    }

  public dist(a: google.maps.LatLngLiteral, b: google.maps.LatLngLiteral): number {
    return google.maps.geometry.spherical.computeDistanceBetween(
        new google.maps.LatLng(a), 
        new google.maps.LatLng(b)
    )
  }   

  public projectOnSegment(p: google.maps.LatLngLiteral, a: google.maps.LatLngLiteral, b: google.maps.LatLngLiteral): { q: google.maps.LatLngLiteral; t: number } {
    const toXY = (ll: google.maps.LatLngLiteral) => {
      const latRad = ll.lat * Math.PI / 180;
      return {
        x: ll.lng,
        y: Math.log(Math.tan((latRad + Math.PI / 2) / 2))
      };
    };

    const PA = toXY(a), PB = toXY(b), PP = toXY(p);
    const vx = PB.x - PA.x, vy = PB.y - PA.y;
    const wx = PP.x - PA.x, wy = PP.y - PA.y;
    const vv = vx * vx + vy * vy || EPS_M;

    let t = (vx * wx + vy * wy) / vv;
    t = Math.max(0, Math.min(1, t));          // clamp

    const qx = PA.x + t * vx;
    const qy = PA.y + t * vy;

    return {
      q: {
        lng: qx,
        lat: (2 * Math.atan(Math.exp(qy)) - Math.PI / 2) * 180 / Math.PI
      },
      t
    };
  }

}